import { useCallback, useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import {
  getGetInventoryVehicleMaintenanceQueryKey,
  getGetInventoryVehicleQueryKey,
  useSaveInventoryVehicleMaintenance,
} from '@/api/generated/inventory-vehicles/inventory-vehicles';
import { VEHICLE_LIST_KEY } from '../internal/vehiclesQueryKeys';
import {
  buildVehicleMaintenanceFormSchema,
  toVehicleMaintenanceRequestBody,
  type VehicleMaintenanceFormValues,
} from './vehicleMaintenanceFormSchema';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  Reference: 'reference',
  TypeCbId: 'type',
  StartDate: 'startDate',
  EndDate: 'endDate',
  Description: 'description',
  CostPln: 'costPln',
  Documents: 'documents',
};

interface UseVehicleMaintenanceFormOptions {
  vehicleId: number;
  initialValues: VehicleMaintenanceFormValues;
  /** Voláno po úspěšném uložení — typicky zavře dialog a zobrazí toast. */
  onSaved: () => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na pole. */
  onError?: (error: unknown) => void;
}

/**
 * Formulář dialogu servisního záznamu: Zod + save mutace + mapování BE chyb.
 * `submit()` = úspěch uložení.
 */
export function useVehicleMaintenanceForm({
  vehicleId,
  initialValues,
  onSaved,
  onError,
}: UseVehicleMaintenanceFormOptions) {
  const { t } = useTranslation('inventory');
  const queryClient = useQueryClient();
  const schema = useMemo(() => buildVehicleMaintenanceFormSchema(t), [t]);
  const saveMutation = useSaveInventoryVehicleMaintenance();
  const successRef = useRef(false);

  const form = useForm({
    defaultValues: initialValues,
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      successRef.current = false;
      try {
        await saveMutation.mutateAsync({
          vehicleId,
          data: toVehicleMaintenanceRequestBody(value),
        });
        // Na rozdíl od dokumentů se ze servisních intervalů odvozuje stav vozu, takže se musí
        // přenačíst i detail a seznam.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetInventoryVehicleMaintenanceQueryKey(vehicleId),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetInventoryVehicleQueryKey(vehicleId),
          }),
          queryClient.invalidateQueries({ queryKey: [VEHICLE_LIST_KEY] }),
        ]);
        successRef.current = true;
        onSaved();
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  const submit = useCallback(async () => {
    await form.handleSubmit();
    return successRef.current;
  }, [form]);

  return { form, submit, isSubmitting: saveMutation.isPending };
}

/** Typ instance TanStack Form dialogu servisu (pro propsy podřízených komponent). */
export type VehicleMaintenanceFormApi = ReturnType<typeof useVehicleMaintenanceForm>['form'];
